import React, { useState, useEffect, useContext } from "react";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  ColumnLayout,
  Container,
  ExpandableSection,
  FormField,
  Input,
  Link,
  Modal,
  Textarea,
  StatusIndicator,
} from "@amzn/awsui-components-react";
import ErrorAlert from "../common/ErrorAlert";
import { CopyToClipboard } from "../common/CopyToClipboard";
import { DlqRedriveInfoApiFactory } from "../../lusca-search-api/LuscaSearchApiFactory";
import { generateMcmUrl } from "../../helpers/logUtils";
import { extractRedriveErrorMessage } from "./dlqRedriveError";
import { StageContext } from "../../contexes/OrderIdContext";
import { Stage } from "../../lusca-search-api/generated-src";
import { StageSelector } from "../redrive/components/StageSelector";
import { FileUploader } from "../redrive/components/FileUploader";
import { useDlqFileValidation } from "./hooks/useDlqFileValidation";
import { useExecuteDlqRedrive } from "./hooks/useExecuteDlqRedrive";
import { useDlqRedriveData } from "./hooks/useDlqRedriveData";
import { useDlqRedriveDetails } from "./hooks/useDlqRedriveDetails";
import { DlqExecutionsTable } from "./table/dlqExecutionsTable";
import { DlqRedriveDetailsTable } from "./table/DlqRedriveDetailsTable";
import { DlqRedriveExecutionDetails } from "./DlqRedriveExecutionDetails";

interface Props {
  setStage: React.Dispatch<React.SetStateAction<Stage>>;
}

// Per-stage localStorage key for a prepared upload, so it survives a tab close
// during the MCM-approval gap.
const uploadStorageKey = (stage: Stage) => `lusca-dlq-redrive-upload-${stage}`;

export default function DlqRedriveContent({ setStage }: Props) {
  const stage = useContext(StageContext);
  const [error, setError] = useState<string | null>(null);
  const [pks, setPks] = useState<string>("");
  const [mcmId, setMcmId] = useState<string>("");
  const [dryRun, setDryRun] = useState<boolean>(false);
  const [deleteWithoutRedriving, setDeleteWithoutRedriving] =
    useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [showRetryConfirm, setShowRetryConfirm] = useState(false);
  const [retryConfirmText, setRetryConfirmText] = useState("");
  const [selectedExecutionArn, setSelectedExecutionArn] = useState<
    string | null
  >(null);

  // The uploaded object referenced by the MCM (persisted in localStorage).
  const [uploadedS3Uri, setUploadedS3Uri] = useState<string | null>(null);
  const [uploadedEtag, setUploadedEtag] = useState<string | null>(null);
  const [uploadedCount, setUploadedCount] = useState<number | null>(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const executionArn = urlParams.get("executionArn");
    if (executionArn) {
      setSelectedExecutionArn(executionArn);
    }
  }, []);

  // Restore (or clear) the prepared upload on stage change — it's stage-specific.
  useEffect(() => {
    let stored: { s3Uri: string; etag: string; count: number } | null = null;
    try {
      const raw = localStorage.getItem(uploadStorageKey(stage));
      if (raw) {
        stored = JSON.parse(raw);
      }
    } catch {
      stored = null;
    }
    if (stored?.s3Uri && stored?.etag) {
      setUploadedS3Uri(stored.s3Uri);
      setUploadedEtag(stored.etag);
      setUploadedCount(typeof stored.count === "number" ? stored.count : null);
      setDeleteWithoutRedriving(true);
    } else {
      setUploadedS3Uri(null);
      setUploadedEtag(null);
      setUploadedCount(null);
    }
  }, [stage]);

  const {
    pastRedrives,
    loading: loadingRedrives,
    error: fetchError,
  } = useDlqRedriveData(stage);
  const {
    details,
    loading: loadingDetails,
    error: detailsError,
  } = useDlqRedriveDetails(stage, selectedExecutionArn);

  const {
    file,
    messageCount,
    error: fileError,
    redriveOK,
    handleFileChange,
    validateRedrive,
  } = useDlqFileValidation();

  const { uploadFile, executeRedrive } = useExecuteDlqRedrive();

  // The literal McmService substring-checks for; only the quoted part is
  // load-bearing, the "Deleting using:" prefix is for template parity.
  const mcmLiteral =
    uploadedS3Uri && uploadedEtag
      ? `Deleting using: "${uploadedS3Uri} ${uploadedEtag}"`
      : null;

  // A prepared upload exists (possibly restored from localStorage, so no `file`).
  const hasPreparedUpload = deleteWithoutRedriving && !!uploadedS3Uri;
  // A file is selected but not yet uploaded — execute must wait.
  const needsUpload = deleteWithoutRedriving && !!file && !uploadedS3Uri;

  const pkList = pks.trim()
    ? pks
        .split("\n")
        .map((id) => id.trim())
        .filter((id) => id)
    : [];
  // Manual-pk path: no S3 file, so the MCM must contain each quoted pk instead.
  const usingManualPks = !file && !hasPreparedUpload && pkList.length > 0;
  const mcmPkLiteral = ["Deleting:", ...pkList.map((id) => `"${id}"`)].join(
    "\n"
  );

  // Only show the steps once there's something to act on (avoids a lone Step 2).
  const showDeleteSteps =
    deleteWithoutRedriving && (!!file || hasPreparedUpload || usingManualPks);

  const persistUpload = (s3Uri: string, etag: string, count: number) => {
    try {
      localStorage.setItem(
        uploadStorageKey(stage),
        JSON.stringify({ s3Uri, etag, count })
      );
    } catch {
      // Non-fatal: persistence is a convenience.
    }
  };

  const clearUpload = () => {
    setUploadedS3Uri(null);
    setUploadedEtag(null);
    setUploadedCount(null);
    try {
      localStorage.removeItem(uploadStorageKey(stage));
    } catch {
      // Ignore storage errors.
    }
  };

  // Swapping the file invalidates the prepared upload (stale s3Uri/etag).
  const onFileChange = (files: File[]) => {
    if (uploadedS3Uri) {
      clearUpload();
    }
    handleFileChange(files);
  };

  // Abandon an in-progress preparation: drop the upload, file, pks and MCM id.
  const handleDiscard = () => {
    clearUpload();
    handleFileChange([]);
    setPks("");
    setMcmId("");
    setError(null);
  };

  // Re-run a failed execution with the same parameters (backend reuses the
  // original execution's input — works after a refresh, no re-upload).
  const handleRetry = async () => {
    if (!selectedExecutionArn) {
      return;
    }
    setRetrying(true);
    setError(null);
    try {
      const dlqRedriveApi = DlqRedriveInfoApiFactory();
      const response = await dlqRedriveApi.retryDLQRedrive(stage, {
        executionArn: selectedExecutionArn,
      });
      const newArn = response.data?.executionArn;
      if (newArn) {
        const urlParams = new URLSearchParams(window.location.search);
        urlParams.set("executionArn", newArn);
        window.history.pushState(
          {},
          "",
          `${window.location.pathname}?${urlParams.toString()}`
        );
        setSelectedExecutionArn(newArn);
      }
    } catch (err) {
      setError(
        `Failed to retry DLQ redrive: ${
          err instanceof Error ? err.message : String(err)
        }`
      );
    } finally {
      setRetrying(false);
      // Always close the modal — on failure this reveals the error alert that
      // would otherwise be hidden behind the open modal.
      setShowRetryConfirm(false);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError("A file must be provided to upload");
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const { s3Uri, etag } = await uploadFile(file, stage);
      if (!etag) {
        setError(
          "Upload succeeded but the S3 ETag could not be read from the response. " +
            "Cannot build the MCM literal — please retry."
        );
        return;
      }
      setUploadedS3Uri(s3Uri);
      setUploadedEtag(etag);
      setUploadedCount(messageCount);
      persistUpload(s3Uri, etag, messageCount);
    } catch (err) {
      setError(
        `Failed to upload file: ${
          err instanceof Error ? err.message : String(err)
        }`
      );
    } finally {
      setUploading(false);
    }
  };

  const handleExecute = async () => {
    if (deleteWithoutRedriving && !mcmId.trim()) {
      setError("MCM ID is required when deleteWithoutRedriving is enabled");
      return;
    }

    // The file must be uploaded first so its s3Uri/etag can go in the MCM.
    if (deleteWithoutRedriving && file && !uploadedS3Uri) {
      setError(
        "Upload the file first, then create + approve the MCM before executing"
      );
      return;
    }

    if (!pks.trim() && !file && !hasPreparedUpload) {
      setError("Either DLQ pks or a file must be provided");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // S3 source and inline pks are mutually exclusive — never send both.
      let messageIdsS3Uri: string | undefined;
      let messageIdsS3Etag: string | undefined;
      let messageIds: string[] | undefined;

      if (file) {
        if (deleteWithoutRedriving) {
          // Reuse the already-uploaded object — no re-upload.
          messageIdsS3Uri = uploadedS3Uri ?? undefined;
          messageIdsS3Etag = uploadedEtag ?? undefined;
        } else {
          // Normal redrive: upload then execute in one click.
          const uploaded = await uploadFile(file, stage);
          messageIdsS3Uri = uploaded.s3Uri;
          messageIdsS3Etag = uploaded.etag;
        }
      } else if (hasPreparedUpload) {
        // Upload restored from localStorage (file gone after a tab close).
        messageIdsS3Uri = uploadedS3Uri ?? undefined;
        messageIdsS3Etag = uploadedEtag ?? undefined;
      } else {
        messageIds = pkList.length > 0 ? pkList : undefined;
      }

      const result = await executeRedrive({
        stage,
        messageIds,
        messageIdsS3Uri,
        messageIdsS3Etag,
        mcmId: mcmId.trim() || undefined,
        dryRun,
        deleteWithoutRedriving,
      });

      setError(null);

      // Clear once the execution has started — a later failure is recoverable
      // via Retry, so the local copy is no longer needed. Dry runs keep it.
      if (!dryRun && hasPreparedUpload) {
        clearUpload();
      }

      if (result?.executionArn) {
        const urlParams = new URLSearchParams(window.location.search);
        urlParams.set("executionArn", result.executionArn);
        window.history.pushState(
          {},
          "",
          `${window.location.pathname}?${urlParams.toString()}`
        );
        setSelectedExecutionArn(result.executionArn);
      } else {
        alert(
          `DLQ redrive ${
            dryRun ? "validation" : "started execution"
          } completed successfully`
        );
      }
    } catch (err) {
      setError(
        `Failed to execute DLQ redrive: ${
          err instanceof Error ? err.message : String(err)
        }`
      );
    } finally {
      setLoading(false);
    }
  };

  const canExecute =
    (pks.trim() || (file && redriveOK) || hasPreparedUpload) && !needsUpload;
  const combinedError =
    [error, fileError, fetchError, detailsError].filter(Boolean).join("; ") ||
    null;

  const handleBackToRedrives = () => {
    setSelectedExecutionArn(null);
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.delete("executionArn");
    window.history.pushState(
      {},
      "",
      `${window.location.pathname}?${urlParams.toString()}`
    );
  };

  if (selectedExecutionArn) {
    return (
      <SpaceBetween size="xl">
        <div>
          <SpaceBetween size="l">
            {combinedError && (
              <ErrorAlert content={combinedError} header="DLQ Redrive Error" />
            )}
            <Button
              iconName="arrow-left"
              onClick={handleBackToRedrives}
              variant="link"
            >
              Back to all redrives
            </Button>
            <DlqRedriveExecutionDetails
              status={details?.status}
              metadata={details?.metadata}
              executionArn={selectedExecutionArn}
              stage={stage}
              loading={!details && loadingDetails}
            />
            {details?.status === "PENDING" && (
              <Box variant="small" color="text-status-inactive">
                The redrive is running. This page updates automatically.
              </Box>
            )}
            {details?.status === "ERROR" &&
              (() => {
                // The cause holds the readable message; result.error is just
                // the exception class. Fall back so we never hide an error.
                const causeMessage = extractRedriveErrorMessage(
                  details.result?.cause
                );
                const friendly =
                  causeMessage ||
                  details.result?.error ||
                  details.error ||
                  "The redrive execution failed.";
                // Only show the raw cause when it adds detail.
                const rawDetail =
                  details.result?.cause &&
                  details.result.cause.trim() !== friendly.trim()
                    ? details.result.cause
                    : null;
                return (
                  <Alert type="error" header="DLQ redrive failed">
                    <SpaceBetween size="s">
                      <Box>{friendly}</Box>
                      {rawDetail && (
                        <ExpandableSection headerText="Raw error details">
                          <Box
                            variant="code"
                            fontSize="body-s"
                            color="text-status-error"
                          >
                            {rawDetail}
                          </Box>
                        </ExpandableSection>
                      )}
                      <Button
                        onClick={() => {
                          setRetryConfirmText("");
                          setShowRetryConfirm(true);
                        }}
                        iconName="refresh"
                        data-testid="retry-redrive"
                      >
                        Retry with same parameters
                      </Button>
                    </SpaceBetween>
                  </Alert>
                );
              })()}
            {details && details.result && (
              <DlqRedriveDetailsTable
                details={details}
                loading={loadingDetails}
              />
            )}
          </SpaceBetween>
          {showRetryConfirm &&
            (() => {
            // A deletion retry gets the destructive treatment: "Delete from
            // DLQ" wording + a type-to-confirm gate. A dry-run delete deletes
            // nothing, so it's treated as a plain (non-destructive) retry.
            const isDeleteRetry =
              !!details?.metadata?.deleteWithoutRedriving &&
              !details?.metadata?.dryRun;
            const confirmDisabled =
              retrying || (isDeleteRetry && retryConfirmText.trim() !== "confirm");
            return (
              <Modal
                visible={showRetryConfirm}
                onDismiss={() => setShowRetryConfirm(false)}
                header={
                  isDeleteRetry ? "Delete from DLQ" : "Retry DLQ redrive"
                }
                footer={
                  <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                      <Button
                        variant="link"
                        onClick={() => setShowRetryConfirm(false)}
                        disabled={retrying}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="primary"
                        onClick={handleRetry}
                        loading={retrying}
                        disabled={confirmDisabled}
                        data-testid="confirm-retry-redrive"
                      >
                        {isDeleteRetry ? "Delete from DLQ" : "Retry"}
                      </Button>
                    </SpaceBetween>
                  </Box>
                }
              >
                <SpaceBetween size="m">
                  <Box>
                    This starts a new execution with the same parameters as the
                    failed one. It will be attributed to you.
                  </Box>
                  {isDeleteRetry && (
                    <Alert type="warning">
                      This is a <b>delete without redrive</b> — it permanently
                      deletes the DLQ items. Ensure the MCM is still approved and
                      In Progress before continuing.
                    </Alert>
                  )}
                  <ColumnLayout columns={2} variant="text-grid">
                    <div>
                      <Box variant="awsui-key-label">Type</Box>
                      <div>{details?.metadata?.requestType ?? "unknown"}</div>
                    </div>
                    <div>
                      <Box variant="awsui-key-label">Source</Box>
                      <div>
                        {details?.metadata?.messageIdsS3Uri ??
                          (details?.metadata?.messageCount != null
                            ? `${details.metadata.messageCount} message id(s)`
                            : "-")}
                      </div>
                    </div>
                    {isDeleteRetry && (
                      <div>
                        <Box variant="awsui-key-label">MCM</Box>
                        <div>
                          {details?.metadata?.mcmId ? (
                            <Link
                              href={generateMcmUrl(
                                stage,
                                details.metadata.mcmId
                              )}
                              external
                              externalIconAriaLabel="Opens in a new tab"
                            >
                              {details.metadata.mcmId}
                            </Link>
                          ) : (
                            "-"
                          )}
                        </div>
                      </div>
                    )}
                  </ColumnLayout>
                  {isDeleteRetry && (
                    <FormField label="To confirm this deletion, type “confirm”.">
                      <Input
                        value={retryConfirmText}
                        onChange={({ detail }) =>
                          setRetryConfirmText(detail.value)
                        }
                        placeholder="confirm"
                      />
                    </FormField>
                  )}
                </SpaceBetween>
              </Modal>
            );
          })()}
        </div>
      </SpaceBetween>
    );
  }

  return (
    <SpaceBetween size="xl">
      <div>
        <SpaceBetween size="l">
          {combinedError && (
            <ErrorAlert content={combinedError} header="DLQ Redrive Error" />
          )}

          <Container header={<Box variant="h2">DLQ Redrive Tool</Box>}>
            <SpaceBetween size="m">
              <Box variant="p">
                This tool allows you to redrive messages from the Dead Letter
                Queue (DLQ). You can provide DLQ pks directly or upload a CSV
                file containing DLQ pks. Use dry run mode to validate without
                actually redriving messages.
              </Box>
              {!selectedExecutionArn && (
                <StageSelector stage={stage} setStage={setStage} />
              )}
            </SpaceBetween>
          </Container>

          {!selectedExecutionArn && (
            <Container>
              <SpaceBetween size="m">
                <Box variant="h2">DLQ Redrive Configuration</Box>

                <FormField
                  label="DLQ pks"
                  description="Enter DLQ pks (primary keys), one per line. Leave empty if uploading a file."
                >
                  <Textarea
                    value={pks}
                    onChange={({ detail }) => setPks(detail.value)}
                    placeholder="pk-1&#10;pk-2&#10;pk-3"
                    rows={5}
                    disabled={!!file || hasPreparedUpload}
                  />
                </FormField>

                <Box variant="p">OR</Box>

                <FileUploader
                  file={file}
                  handleFileChange={onFileChange}
                  validateRedrive={validateRedrive}
                  redriveOK={redriveOK}
                  loading={loading}
                />

                {file && redriveOK && (
                  <Box>File validated ({messageCount} DLQ pks)</Box>
                )}

                <SpaceBetween size="xs">
                  <Checkbox
                    checked={dryRun}
                    onChange={({ detail }) => setDryRun(detail.checked)}
                  >
                    Dry Run (validate without executing)
                  </Checkbox>

                  <Checkbox
                    checked={deleteWithoutRedriving}
                    onChange={({ detail }) => {
                      setDeleteWithoutRedriving(detail.checked);
                      if (!detail.checked && uploadedS3Uri) {
                        clearUpload();
                      }
                    }}
                  >
                    Delete Without Redriving
                  </Checkbox>
                </SpaceBetween>

                {showDeleteSteps && (
                    <Container
                      header={
                        <Box variant="h3">Step 1: Prepare MCM</Box>
                      }
                    >
                      <SpaceBetween size="m">
                        {usingManualPks ? (
                          <>
                            <Box variant="p">
                              Create an MCM using this{" "}
                              <a
                                href="https://mcm.amazon.com/templates/86285ba0-0964-489e-9818-6cf33ad23dd1"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                template
                              </a>{" "}
                              (title containing &quot;delete without
                              redrive&quot;) whose description contains each pk
                              below (with the surrounding double-quotes), and get
                              it approved and In Progress.
                            </Box>

                            <FormField
                              label="MCM description literal (paste into the MCM, ideally in a code block)"
                              secondaryControl={
                                <CopyToClipboard
                                  getContent={() =>
                                    Promise.resolve(mcmPkLiteral)
                                  }
                                  iconName="copy"
                                  ariaLabel="Copy MCM pk literal"
                                >
                                  Copy
                                </CopyToClipboard>
                              }
                            >
                              <Textarea
                                value={mcmPkLiteral}
                                readOnly
                                rows={Math.min(pkList.length + 1, 8)}
                              />
                            </FormField>

                            <Alert type="info">
                              Include the surrounding double-quotes exactly as
                              shown.
                            </Alert>
                          </>
                        ) : (
                          <>
                            <Box variant="p">
                              Upload the file, then create an MCM using this{" "}
                              <a
                                href="https://mcm.amazon.com/templates/86285ba0-0964-489e-9818-6cf33ad23dd1"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                template
                              </a>{" "}
                              (title containing &quot;delete without
                              redrive&quot;) whose description contains the
                              literal below, and get it approved and In Progress.
                              Do not re-upload — that changes the ETag and the
                              MCM will no longer match.
                            </Box>

                            {file && (
                              <Button
                                onClick={handleUpload}
                                disabled={
                                  !redriveOK || uploading || !!uploadedS3Uri
                                }
                                loading={uploading}
                              >
                                {uploadedS3Uri
                                  ? "Uploaded"
                                  : "Upload file for MCM"}
                              </Button>
                            )}

                            {uploadedS3Uri && mcmLiteral && (
                              <SpaceBetween size="s">
                                <StatusIndicator type="success">
                                  Uploaded
                                  {uploadedCount !== null
                                    ? ` (${uploadedCount} DLQ pks)`
                                    : ""}
                                </StatusIndicator>

                                <FormField
                                  label="S3 URI"
                                  secondaryControl={
                                    <CopyToClipboard
                                      getContent={() =>
                                        Promise.resolve(uploadedS3Uri)
                                      }
                                      iconName="copy"
                                      ariaLabel="Copy S3 URI"
                                    />
                                  }
                                >
                                  <Input value={uploadedS3Uri} readOnly />
                                </FormField>

                                <FormField
                                  label="ETag"
                                  secondaryControl={
                                    <CopyToClipboard
                                      getContent={() =>
                                        Promise.resolve(uploadedEtag ?? "")
                                      }
                                      iconName="copy"
                                      ariaLabel="Copy ETag"
                                    />
                                  }
                                >
                                  <Input value={uploadedEtag ?? ""} readOnly />
                                </FormField>

                                <FormField
                                  label="MCM description literal (paste into the MCM, ideally in a code block)"
                                  secondaryControl={
                                    <CopyToClipboard
                                      getContent={() =>
                                        Promise.resolve(mcmLiteral)
                                      }
                                      iconName="copy"
                                      ariaLabel="Copy MCM literal"
                                    >
                                      Copy
                                    </CopyToClipboard>
                                  }
                                >
                                  <Input value={mcmLiteral} readOnly />
                                </FormField>

                                <Alert type="info">
                                  Include the surrounding double-quotes exactly
                                  as shown.
                                </Alert>
                              </SpaceBetween>
                            )}
                          </>
                        )}
                      </SpaceBetween>
                    </Container>
                  )}

                {showDeleteSteps ? (
                  <Container
                    header={
                      <Box variant="h3">
                        Step 2: Execute with approved MCM
                      </Box>
                    }
                  >
                    <SpaceBetween size="m">
                      <Box variant="p">
                        Once the MCM is approved and In Progress, enter its ID
                        and execute.
                        {usingManualPks
                          ? ""
                          : " The redrive reuses the file already uploaded above — do not re-upload."}
                      </Box>

                      <FormField label="MCM ID">
                        <Input
                          value={mcmId}
                          onChange={({ detail }) => setMcmId(detail.value)}
                          placeholder="MCM-XXXXXXXX"
                        />
                      </FormField>

                      <SpaceBetween size="xs" direction="horizontal">
                        <Button
                          onClick={handleExecute}
                          variant="primary"
                          disabled={!canExecute || loading}
                          loading={loading}
                          data-testid="execute-redrive"
                        >
                          {dryRun ? "Validate Redrive" : "Execute Redrive"}
                        </Button>
                        <Button
                          onClick={handleDiscard}
                          disabled={loading}
                          data-testid="discard-redrive"
                        >
                          Discard
                        </Button>
                      </SpaceBetween>
                    </SpaceBetween>
                  </Container>
                ) : (
                  <Button
                    onClick={handleExecute}
                    variant="primary"
                    disabled={!canExecute || loading}
                    loading={loading}
                    data-testid="execute-redrive"
                  >
                    {dryRun ? "Validate Redrive" : "Execute Redrive"}
                  </Button>
                )}
              </SpaceBetween>
            </Container>
          )}

          <DlqExecutionsTable items={pastRedrives} loading={loadingRedrives} />
        </SpaceBetween>
      </div>
    </SpaceBetween>
  );
}
